import React from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import LandingPage from '@/presentation/landing/LandingPage';
import RegisterPage from '@/presentation/auth/RegisterPage';
import LoginPage from '@/presentation/auth/LoginPage';
import StudentDashboard from '@/presentation/dashboard/StudentDashboard';
import TeacherDashboard from '@/presentation/dashboard/TeacherDashboard';
import ParentDashboard from '@/presentation/dashboard/ParentDashboard';
import AdminDashboard from '@/presentation/dashboard/AdminDashboard';
import RoleDashboardShell from '@/presentation/dashboard/RoleDashboardShell';
import { useRoleDashboardConfig } from '@/presentation/dashboard/useRoleDashboardConfig';
import { useAuthSessionStore } from '@/presentation/auth/useAuthSessionStore';
import AccountManagementPage from '@/presentation/workspace/pages/AccountManagementPage';
import SettingsManagementPage from '@/presentation/workspace/pages/SettingsManagementPage';
import NotificationsManagementPage from '@/presentation/workspace/pages/NotificationsManagementPage';
import QuizHistoryPage from '@/presentation/workspace/pages/QuizHistoryPage';
import QuizHistoryDetailPage from '@/presentation/workspace/pages/QuizHistoryDetailPage';
import ClassManagementPage from '@/presentation/workspace/pages/ClassManagementPage';
import ClassDetailPage from '@/presentation/workspace/pages/ClassDetailPage';
import ClassStudentDetailPage from '@/presentation/workspace/pages/ClassStudentDetailPage';
import ClassQuizResultsPage from '@/presentation/workspace/pages/ClassQuizResultsPage';
import ChildrenManagementPage from '@/presentation/workspace/pages/ChildrenManagementPage';
import ChildLearningDetailPage from '@/presentation/workspace/pages/ChildLearningDetailPage';
import QuizEditorPage from '@/presentation/quiz/QuizEditorPage';
import QuizManagementPage from '@/presentation/quiz/QuizManagementPage';
import PublicQuizPlayPage from '@/presentation/quiz/PublicQuizPlayPage';
import PublicQuizDetailPage from '@/presentation/quiz/PublicQuizDetailPage';
import QuizGoogleSheetGuidePage from '@/presentation/quiz/QuizGoogleSheetGuidePage';

function RequireSession({ children }) {
  const session = useAuthSessionStore((s) => s.session);
  if (!session) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function RequireAuthenticated({ children }) {
  const session = useAuthSessionStore((s) => s.session);
  if (!session) {
    return <Navigate to="/" replace />;
  }
  if (!session?.accessToken) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function LandingRoute() {
  const navigate = useNavigate();
  const session = useAuthSessionStore((s) => s.session);
  const startGuestSession = useAuthSessionStore((s) => s.startGuestSession);

  if (session) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <LandingPage
      onStart={() => navigate('/register')}
      onLogin={() => navigate('/login')}
      onGuest={() => {
        startGuestSession();
        navigate('/dashboard');
      }}
    />
  );
}

function RegisterRoute() {
  const navigate = useNavigate();
  const session = useAuthSessionStore((s) => s.session);
  const setAuthenticatedSession = useAuthSessionStore((s) => s.setAuthenticatedSession);

  if (session?.accessToken) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <RegisterPage
      onLogin={() => navigate('/login')}
      onRegistered={(result, role) => {
        setAuthenticatedSession(result, 'registered', role);
        navigate('/dashboard');
      }}
    />
  );
}

function LoginRoute() {
  const navigate = useNavigate();
  const session = useAuthSessionStore((s) => s.session);
  const setAuthenticatedSession = useAuthSessionStore((s) => s.setAuthenticatedSession);

  if (session?.accessToken) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <LoginPage
      onRegister={() => navigate('/register')}
      onLoggedIn={(result) => {
        setAuthenticatedSession(result, 'authenticated');
        navigate('/dashboard');
      }}
    />
  );
}

function DashboardRoute() {
  const navigate = useNavigate();
  const session = useAuthSessionStore((s) => s.session);
  const clearSession = useAuthSessionStore((s) => s.clearSession);
  const logout = () => {
    clearSession();
    navigate('/login');
  };

  if (session?.role === 'teacher') {
    return <TeacherDashboard session={session} onLogout={logout} />;
  }
  if (session?.role === 'parent') {
    return <ParentDashboard session={session} onLogout={logout} />;
  }
  if (session?.role === 'admin' || session?.role === 'super_admin') {
    return <AdminDashboard session={session} onLogout={logout} />;
  }
  return <StudentDashboard session={session} onLogout={logout} />;
}

function WorkspaceManagementRoute({ page }) {
  const navigate = useNavigate();
  const session = useAuthSessionStore((s) => s.session);
  const clearSession = useAuthSessionStore((s) => s.clearSession);
  const config = useRoleDashboardConfig(session);

  const onLogout = () => {
    clearSession();
    navigate('/login');
  };

  let content = null;
  if (page === 'account') {
    content = <AccountManagementPage session={session} />;
  } else if (page === 'settings') {
    content = <SettingsManagementPage session={session} />;
  } else if (page === 'history') {
    content = <QuizHistoryPage session={session} />;
  } else {
    content = <NotificationsManagementPage session={session} />;
  }

  return (
    <RoleDashboardShell
      session={session}
      appTitle={config.appTitle}
      systemTagline={config.systemTagline}
      roleLabel={config.roleLabel}
      menuItems={config.menuItems}
      onLogout={onLogout}
    >
      {content}
    </RoleDashboardShell>
  );
}

function QuizCreateRoute() {
  const session = useAuthSessionStore((s) => s.session);
  const navigate = useNavigate();
  const clearSession = useAuthSessionStore((s) => s.clearSession);
  const config = useRoleDashboardConfig(session);
  const onLogout = () => {
    clearSession();
    navigate('/login');
  };
  return (
    <RoleDashboardShell
      session={session}
      appTitle={config.appTitle}
      systemTagline={config.systemTagline}
      roleLabel={config.roleLabel}
      menuItems={config.menuItems}
      onLogout={onLogout}
    >
      <QuizEditorPage session={session} />
    </RoleDashboardShell>
  );
}

function QuizEditRoute() {
  const { quizId } = useParams();
  const session = useAuthSessionStore((s) => s.session);
  const navigate = useNavigate();
  const clearSession = useAuthSessionStore((s) => s.clearSession);
  const config = useRoleDashboardConfig(session);
  const onLogout = () => {
    clearSession();
    navigate('/login');
  };
  return (
    <RoleDashboardShell
      session={session}
      appTitle={config.appTitle}
      systemTagline={config.systemTagline}
      roleLabel={config.roleLabel}
      menuItems={config.menuItems}
      onLogout={onLogout}
    >
      <QuizEditorPage session={session} quizId={quizId} />
    </RoleDashboardShell>
  );
}

function QuizManageRoute() {
  const session = useAuthSessionStore((s) => s.session);
  const navigate = useNavigate();
  const clearSession = useAuthSessionStore((s) => s.clearSession);
  const config = useRoleDashboardConfig(session);
  const onLogout = () => {
    clearSession();
    navigate('/login');
  };
  return (
    <RoleDashboardShell
      session={session}
      appTitle={config.appTitle}
      systemTagline={config.systemTagline}
      roleLabel={config.roleLabel}
      menuItems={config.menuItems}
      onLogout={onLogout}
    >
      <QuizManagementPage session={session} />
    </RoleDashboardShell>
  );
}

function QuizHistoryDetailRoute() {
  const { resultId } = useParams();
  const session = useAuthSessionStore((s) => s.session);
  const navigate = useNavigate();
  const clearSession = useAuthSessionStore((s) => s.clearSession);
  const config = useRoleDashboardConfig(session);
  const onLogout = () => {
    clearSession();
    navigate('/login');
  };
  return (
    <RoleDashboardShell
      session={session}
      appTitle={config.appTitle}
      systemTagline={config.systemTagline}
      roleLabel={config.roleLabel}
      menuItems={config.menuItems}
      onLogout={onLogout}
    >
      <QuizHistoryDetailPage session={session} resultId={resultId} />
    </RoleDashboardShell>
  );
}

function ClassesRoute() {
  const session = useAuthSessionStore((s) => s.session);
  const navigate = useNavigate();
  const clearSession = useAuthSessionStore((s) => s.clearSession);
  const config = useRoleDashboardConfig(session);
  const onLogout = () => {
    clearSession();
    navigate('/login');
  };
  return (
    <RoleDashboardShell
      session={session}
      appTitle={config.appTitle}
      systemTagline={config.systemTagline}
      roleLabel={config.roleLabel}
      menuItems={config.menuItems}
      onLogout={onLogout}
    >
      <ClassManagementPage session={session} />
    </RoleDashboardShell>
  );
}

function ClassDetailRoute() {
  const { classId } = useParams();
  const session = useAuthSessionStore((s) => s.session);
  const navigate = useNavigate();
  const clearSession = useAuthSessionStore((s) => s.clearSession);
  const config = useRoleDashboardConfig(session);
  const onLogout = () => {
    clearSession();
    navigate('/login');
  };
  return (
    <RoleDashboardShell
      session={session}
      appTitle={config.appTitle}
      systemTagline={config.systemTagline}
      roleLabel={config.roleLabel}
      menuItems={config.menuItems}
      onLogout={onLogout}
    >
      <ClassDetailPage session={session} classId={classId} />
    </RoleDashboardShell>
  );
}

function ClassStudentDetailRoute() {
  const { classId, studentId } = useParams();
  const session = useAuthSessionStore((s) => s.session);
  const navigate = useNavigate();
  const clearSession = useAuthSessionStore((s) => s.clearSession);
  const config = useRoleDashboardConfig(session);
  const onLogout = () => {
    clearSession();
    navigate('/login');
  };
  return (
    <RoleDashboardShell
      session={session}
      appTitle={config.appTitle}
      systemTagline={config.systemTagline}
      roleLabel={config.roleLabel}
      menuItems={config.menuItems}
      onLogout={onLogout}
    >
      <ClassStudentDetailPage session={session} classId={classId} studentId={studentId} />
    </RoleDashboardShell>
  );
}

function ClassQuizResultsRoute() {
  const { classId, quizId } = useParams();
  const session = useAuthSessionStore((s) => s.session);
  const navigate = useNavigate();
  const clearSession = useAuthSessionStore((s) => s.clearSession);
  const config = useRoleDashboardConfig(session);
  const onLogout = () => {
    clearSession();
    navigate('/login');
  };
  return (
    <RoleDashboardShell
      session={session}
      appTitle={config.appTitle}
      systemTagline={config.systemTagline}
      roleLabel={config.roleLabel}
      menuItems={config.menuItems}
      onLogout={onLogout}
    >
      <ClassQuizResultsPage session={session} classId={classId} quizId={quizId} />
    </RoleDashboardShell>
  );
}

function ChildrenRoute() {
  const session = useAuthSessionStore((s) => s.session);
  const navigate = useNavigate();
  const clearSession = useAuthSessionStore((s) => s.clearSession);
  const config = useRoleDashboardConfig(session);
  const onLogout = () => {
    clearSession();
    navigate('/login');
  };
  return (
    <RoleDashboardShell
      session={session}
      appTitle={config.appTitle}
      systemTagline={config.systemTagline}
      roleLabel={config.roleLabel}
      menuItems={config.menuItems}
      onLogout={onLogout}
    >
      <ChildrenManagementPage session={session} />
    </RoleDashboardShell>
  );
}

function ChildLearningDetailRoute() {
  const { studentId } = useParams();
  const session = useAuthSessionStore((s) => s.session);
  const navigate = useNavigate();
  const clearSession = useAuthSessionStore((s) => s.clearSession);
  const config = useRoleDashboardConfig(session);
  const onLogout = () => {
    clearSession();
    navigate('/login');
  };
  return (
    <RoleDashboardShell
      session={session}
      appTitle={config.appTitle}
      systemTagline={config.systemTagline}
      roleLabel={config.roleLabel}
      menuItems={config.menuItems}
      onLogout={onLogout}
    >
      <ChildLearningDetailPage session={session} studentId={studentId} />
    </RoleDashboardShell>
  );
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingRoute />} />
      <Route path="/register" element={<RegisterRoute />} />
      <Route path="/login" element={<LoginRoute />} />
      <Route
        path="/dashboard"
        element={
          <RequireSession>
            <DashboardRoute />
          </RequireSession>
        }
      />
      <Route
        path="/account"
        element={
          <RequireAuthenticated>
            <WorkspaceManagementRoute page="account" />
          </RequireAuthenticated>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireSession>
            <WorkspaceManagementRoute page="settings" />
          </RequireSession>
        }
      />
      <Route
        path="/history"
        element={
          <RequireSession>
            <WorkspaceManagementRoute page="history" />
          </RequireSession>
        }
      />
      <Route
        path="/history/:resultId"
        element={
          <RequireSession>
            <QuizHistoryDetailRoute />
          </RequireSession>
        }
      />
      <Route
        path="/notifications"
        element={
          <RequireSession>
            <WorkspaceManagementRoute page="notifications" />
          </RequireSession>
        }
      />
      <Route
        path="/classes"
        element={
          <RequireSession>
            <ClassesRoute />
          </RequireSession>
        }
      />
      <Route
        path="/classes/:classId"
        element={
          <RequireSession>
            <ClassDetailRoute />
          </RequireSession>
        }
      />
      <Route
        path="/classes/:classId/students/:studentId"
        element={
          <RequireSession>
            <ClassStudentDetailRoute />
          </RequireSession>
        }
      />
      <Route
        path="/classes/:classId/quizzes/:quizId"
        element={
          <RequireSession>
            <ClassQuizResultsRoute />
          </RequireSession>
        }
      />
      <Route
        path="/children"
        element={
          <RequireSession>
            <ChildrenRoute />
          </RequireSession>
        }
      />
      <Route
        path="/children/:studentId"
        element={
          <RequireSession>
            <ChildLearningDetailRoute />
          </RequireSession>
        }
      />
      <Route
        path="/quizzes/create"
        element={
          <RequireAuthenticated>
            <QuizCreateRoute />
          </RequireAuthenticated>
        }
      />
      <Route
        path="/quizzes/manage"
        element={
          <RequireAuthenticated>
            <QuizManageRoute />
          </RequireAuthenticated>
        }
      />
      <Route
        path="/quizzes/:quizId/edit"
        element={
          <RequireAuthenticated>
            <QuizEditRoute />
          </RequireAuthenticated>
        }
      />
      <Route path="/play/:code" element={<PublicQuizPlayPage />} />
      <Route path="/public-quizzes/:code" element={<PublicQuizDetailPage />} />
      <Route path="/help/quiz-google-sheet" element={<QuizGoogleSheetGuidePage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

